/**
 * 【法币展示/决策 统一逻辑】法币区域 / Fiat-M 与首页资产展示
 * 统一只信 recommendedCurrencyList：
 * - plates 中包含 258 的币种视为法币（用于资产展示、充值弹窗 IP 选中等）
 * - 仅 isSymbolExist=1 的法币才参与 Fiat-M / 交易默认跳转（如 VND 无交易对则排除）
 * - 不再做前端本地 IP / 余额 / fiatCurrencyIds 兜底筛选
 */
import { Storage, Util, Request } from "@xt/xtbase";

const { getOrigin } = Util;
const { XtAxios4 } = Request;

const IP_API_URL = getOrigin("www") + "/sapi/v4/ip/public/info/ip";
const RECOMMENDED_FIAT_PLATE_ID = 258;

function hasFiatTradeSymbol(item) {
  return Number(item?.isSymbolExist) === 1;
}

function isRecommendedFiatItem(item) {
  const plates = Array.isArray(item?.plates) ? item.plates : [];
  return plates.some((plateId) => Number(plateId?.id ?? plateId) === RECOMMENDED_FIAT_PLATE_ID);
}

/**
 * 从 fund.recommendedCurrencyList（或同结构数组）解析推荐法币代码，与 Market 页 plate 258 规则一致。
 * @param {Array<{ currency?: string, coinName?: string, plates?: number[] }>} recommendedCurrencyList
 * @returns {string[]}
 */
/** 展示用法币：plate 258，不要求存在交易对（含 VND 充值/资产展示） */
export function getRecommendedFiatCodesFromList(recommendedCurrencyList = []) {
  if (!Array.isArray(recommendedCurrencyList)) return [];
  return recommendedCurrencyList.reduce((result, item) => {
    const code = String(item?.currency || item?.coinName || "").toUpperCase();
    if (isRecommendedFiatItem(item) && code && !result.includes(code)) {
      result.push(code);
    }
    return result;
  }, []);
}

/** 可交易法币：plate 258 且 isSymbolExist=1（Fiat-M / CFD 默认跳转） */
function getTradableFiatCodesFromList(recommendedCurrencyList = []) {
  if (!Array.isArray(recommendedCurrencyList)) return [];
  return recommendedCurrencyList.reduce((result, item) => {
    const code = String(item?.currency || item?.coinName || "").toUpperCase();
    if (isRecommendedFiatItem(item) && hasFiatTradeSymbol(item) && code && !result.includes(code)) {
      result.push(code);
    }
    return result;
  }, []);
}

/** 测试用：设为 "AE" 等时 IP 接口被跳过，直接返回该国家代码；设为 null 使用真实 IP */
const IP_MOCK_COUNTRY = null;

function getRecommendedCurrencies() {
  const payload = Storage.get("recommendedCurrencyList") || {};
  return Array.isArray(payload.currencies) ? payload.currencies : [];
}

export function getDynamicFiatMarketCodes() {
  return getTradableFiatCodesFromList(getRecommendedCurrencies());
}

export function isFiatMarketCode(code) {
  if (!code) return false;
  return getDynamicFiatMarketCodes().includes(String(code).toUpperCase());
}

function getCurrencyMapFromRecommendedCurrencies() {
  const currencies = getRecommendedCurrencies();
  const map = {};
  currencies.forEach((item) => {
    const id = item.id ?? item.currencyId ?? item.coinId;
    const code = item.currency || item.currencyCode;
    if (id != null && code && isRecommendedFiatItem(item)) {
      map[id] = (code + "").toUpperCase();
    }
  });
  return map;
}

/**
 * 从 recommendedCurrencyList 构建 coinId -> 币种代码 的映射
 * @returns {Record<string, string>}
 */
export function getFiatCurrencyMapFromStorage() {
  return getCurrencyMapFromRecommendedCurrencies();
}

/**
 * 通过 IP 获取国家代码
 * @returns {Promise<string|null>} 如 "AE", "BR", "SA"
 */
export async function getCountryCodeByIP() {
  try {
    if (IP_MOCK_COUNTRY) return IP_MOCK_COUNTRY;

    const response = await XtAxios4.get(IP_API_URL);
    const countryCode = response?.countryCode || response?.data?.countryCode;
    return countryCode ? countryCode.toUpperCase() : null;
  } catch (error) {
    console.warn("fiatMarket: getCountryCodeByIP failed", error);
    return null;
  }
}

/**
 * 兼容旧返回结构：法币列表直接来自 recommendedCurrencyList
 * @param {{ fiatCurrencyIds?: number[] }} options
 * @returns {Promise<{ fromFiatIds: string[], fromIP: string | null }>}
 */
export async function resolveFiatCodesForList(options = {}) {
  const allowedCodes = getRecommendedFiatCodesFromList(getRecommendedCurrencies());
  return { fromFiatIds: allowedCodes, fromIP: null };
}

/**
 * 【法币提现专用】返回允许展示的法币列表及默认选中
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ allowedCodes: string[], primaryFiat: string }>}
 */
export async function resolveAllowedFiatCodesForWithdraw(options = {}) {
  const displayCodes = getRecommendedFiatCodesFromList(getRecommendedCurrencies());
  return { allowedCodes: displayCodes, primaryFiat: displayCodes[0] || "USDT" };
}

/**
 * 返回允许展示的法币代码列表，用于 Convert/充值/提现等
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ allowedCodes: string[], fromFiatIds: string[], fromIP: string | null }>}
 */
export async function resolveAllowedFiatCodesForDisplay(options = {}) {
  const displayCodes = getRecommendedFiatCodesFromList(getRecommendedCurrencies());
  return { allowedCodes: displayCodes, fromFiatIds: displayCodes, fromIP: null };
}

/**
 * 判断是否应展示法币相关 UI（如 Fiat-M tab），并返回允许展示的法币代码列表
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options fiatCodesWithBalance 为有法币余额的代码列表，需调用方拉取后传入
 * @returns {Promise<{ showFiatTab: boolean, fiatCode?: string, fiatCodes: string[] }>}
 */
export async function resolveFiatMarketEligible(options = {}) {
  const dynamicCodes = getDynamicFiatMarketCodes();
  const showFiatTab = dynamicCodes.length > 0;
  return {
    showFiatTab,
    fiatCode: dynamicCodes[0] || undefined,
    fiatCodes: dynamicCodes,
  };
}

/**
 * 解析余额/汇率等场景下的法币选项
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ options: string[], primaryFiat: string, selectable: boolean }>}
 */
export async function resolveFiatOptionsForBalance(options = {}) {
  const displayCodes = getRecommendedFiatCodesFromList(getRecommendedCurrencies());
  const options_ = displayCodes.length > 0 ? ["USDT", ...displayCodes] : ["USDT"];
  const primaryFiat = displayCodes[0] || "USDT";
  const selectable = options_.length > 1;
  return { options: options_, primaryFiat, selectable };
}
